import React, { useState, useEffect } from 'react';
import { Alert, Card, Col, Row, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import classNames from 'classnames';
import { useAuth } from 'react-oidc-context';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { DELETE_LISTING, GET_LISTINGS } from 'graphql/listing';
import { setLocation } from 'reducers/createListingReducer';
import CreateListingModal from './create-listing';
import { AnnouncementModal, OkCancelModal } from 'components/common/Modals';
import ListingGridItem from '../../common/listing/listing-item/ListingGridItem';

const MyListings = () => {
  const auth = useAuth();
  const dispatch = useDispatch();

  const { data } = useQuery(GET_LISTINGS, {
    variables: {
      createdBy: auth.user.profile.sub
    }
  });

  const [mutateFunction] = useMutation(DELETE_LISTING, {
    refetchQueries: [GET_LISTINGS]
  });

  const { address } = useSelector(state => state.profile);

  const [listings, setListings] = useState([]);

  const [listType] = useState('grid');
  const [createListingModalVisible, setCreateListingModalVisible] =
    useState(false);

  const [remindLocationModalVisible, setRemindLocationVisible] =
    useState(false);

  const [deleteConfirmationModalVisible, setDeleteConfirmationModalVisible] =
    useState(false);
  const [deleteConfirmationListing, setDeleteConfirmationListing] =
    useState(null);

  useEffect(() => {
    dispatch(setLocation(address));
  }, [address]);

  useEffect(() => {
    if (!data) {
      return;
    }
    setListings(data.listings.items);
  }, [data]);

  const handleCloseCreateListingModel = () => {
    setCreateListingModalVisible(false);
  };

  const handleDeleteConfirmation = listing => {
    setDeleteConfirmationListing(listing);
    setDeleteConfirmationModalVisible(true);
  };

  const handleDelete = () => {
    if (deleteConfirmationListing) {
      mutateFunction({
        variables: {
          input: {
            listingId: deleteConfirmationListing.id
          }
        },
        onCompleted: () => {
          setDeleteConfirmationModalVisible(false);
          toast.success('Listing deleted');
        }
      });
    }
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row className="flex-between-center g-2">
            <Col md>
              <h5 className="mb-2 mb-md-0">My Listings</h5>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-primary"
                type="submit"
                onClick={() => {
                  if (!address) {
                    setRemindLocationVisible(true);
                    return;
                  }
                  setCreateListingModalVisible(true);
                }}
              >
                Create a listing
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card>
        <Card.Body
          className={classNames({
            'p-0 overflow-hidden': listType === 'list',
            'pb-0': listType === 'grid'
          })}
        >
          <Row
            className={classNames({
              'g-0': listType === 'list'
            })}
          >
            {listings.length === 0 && <p>You have not created any listing.</p>}
            {listings?.map(listing => (
              <ListingGridItem
                listing={listing}
                key={listing.id}
                onDelete={() => handleDeleteConfirmation(listing)}
                md={4}
                lg={3}
                myListingMode={true}
              />
            ))}
          </Row>
        </Card.Body>
        <Card.Footer
          className={classNames('d-flex justify-content-center', {
            'bg-body-tertiary mt-n1': listType === 'grid',
            'border-top': listType === 'list'
          })}
        ></Card.Footer>
      </Card>
      <AnnouncementModal
        size="lg"
        show={remindLocationModalVisible}
        onClose={() => setRemindLocationVisible(false)}
        title={'Information'}
        content={
          <>
            You have not set a default location. Please update your{' '}
            <Link variant="secondary" to="/user/profile">
              default location
            </Link>{' '}
            before creating a listing.
          </>
        }
      />
      <OkCancelModal
        show={deleteConfirmationModalVisible}
        size="lg"
        onOk={() => handleDelete()}
        onCancel={() => setDeleteConfirmationModalVisible(false)}
        title={'Delete'}
        content={
          <Alert variant="danger">
            Are you sure you want to delete listing{' '}
            {deleteConfirmationListing?.title}?
          </Alert>
        }
      />
      <CreateListingModal
        show={createListingModalVisible}
        onClose={handleCloseCreateListingModel}
      />
    </>
  );
};

export default MyListings;
